import * as Sentry from '@sentry/react'
import {APILoadingStatus, APIProvider, useApiLoadingStatus} from '@vis.gl/react-google-maps'
import {createContext, ReactNode, useCallback, useContext, useEffect, useState} from 'react'

type GoogleMapsProviderProps = {
    children: ReactNode
}

export type GoogleMapsProviderStatus =
    | 'missing'
    | 'loading'
    | 'ready'
    | 'script-load-failed'
    | 'authentication-failed'

const GOOGLE_MAPS_LIBRARIES = ['places', 'geocoding', 'marker']
const MAP_PROVIDER = 'google_maps'
const reportedFailures = new Set<'script_load' | 'authentication'>()
const authFailureListeners = new Set<() => void>()
let authFailureHandlerInstalled = false
let previousAuthFailure: (() => void) | undefined

const GoogleMapsProviderStatusContext = createContext<GoogleMapsProviderStatus>('missing')

function reportProviderFailure(source: 'script_load' | 'authentication') {
    if (reportedFailures.has(source)) {
        return
    }
    reportedFailures.add(source)
    Sentry.captureException(new Error(`Google Maps ${source} failure`), {
        tags: {map_provider: MAP_PROVIDER, failure_source: source},
    })
}

function notifyAuthenticationFailure() {
    previousAuthFailure?.()
    reportProviderFailure('authentication')
    authFailureListeners.forEach((listener) => listener())
}

function subscribeToAuthenticationFailure(listener: () => void) {
    authFailureListeners.add(listener)
    if (!authFailureHandlerInstalled) {
        const windowWithAuthFailure = window as Window & {gm_authFailure?: () => void}
        previousAuthFailure = windowWithAuthFailure.gm_authFailure
        windowWithAuthFailure.gm_authFailure = notifyAuthenticationFailure
        authFailureHandlerInstalled = true
    }

    return () => {
        authFailureListeners.delete(listener)
        if (authFailureListeners.size === 0 && authFailureHandlerInstalled) {
            const windowWithAuthFailure = window as Window & {gm_authFailure?: () => void}
            windowWithAuthFailure.gm_authFailure = previousAuthFailure
            previousAuthFailure = undefined
            authFailureHandlerInstalled = false
        }
    }
}

function GoogleMapsProviderContent({
    children,
    scriptLoadFailed,
}: {
    children: ReactNode
    scriptLoadFailed: boolean
}) {
    const loadingStatus = useApiLoadingStatus()
    const [authenticationFailed, setAuthenticationFailed] = useState(false)

    useEffect(() => subscribeToAuthenticationFailure(() => setAuthenticationFailed(true)), [])
    useEffect(() => {
        if (loadingStatus === APILoadingStatus.AUTH_FAILURE) {
            reportProviderFailure('authentication')
            setAuthenticationFailed(true)
        }
        if (loadingStatus === APILoadingStatus.FAILED) {
            reportProviderFailure('script_load')
        }
    }, [loadingStatus])

    const status = authenticationFailed
        ? 'authentication-failed'
        : scriptLoadFailed || loadingStatus === APILoadingStatus.FAILED
          ? 'script-load-failed'
          : loadingStatus === APILoadingStatus.LOADED
            ? 'ready'
            : 'loading'

    return (
        <GoogleMapsProviderStatusContext.Provider value={status}>
            {children}
        </GoogleMapsProviderStatusContext.Provider>
    )
}

export function GoogleMapsProvider({children}: GoogleMapsProviderProps) {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
    const hasApiKey = Boolean(apiKey?.trim())

    if (!hasApiKey) {
        return (
            <GoogleMapsProviderStatusContext.Provider value="missing">
                {children}
            </GoogleMapsProviderStatusContext.Provider>
        )
    }

    return <ConfiguredGoogleMapsProvider apiKey={apiKey}>{children}</ConfiguredGoogleMapsProvider>
}

function ConfiguredGoogleMapsProvider({apiKey, children}: {apiKey: string; children: ReactNode}) {
    const [scriptLoadFailed, setScriptLoadFailed] = useState(false)
    const handleError = useCallback((error: unknown) => {
        setScriptLoadFailed(true)
        reportProviderFailure('script_load')
        void error
    }, [])

    return (
        <APIProvider apiKey={apiKey} libraries={GOOGLE_MAPS_LIBRARIES} onError={handleError}>
            <GoogleMapsProviderContent scriptLoadFailed={scriptLoadFailed}>
                {children}
            </GoogleMapsProviderContent>
        </APIProvider>
    )
}

export function useGoogleMapsProviderStatus() {
    return useContext(GoogleMapsProviderStatusContext)
}

export function getGoogleMapsApiKey() {
    return import.meta.env.VITE_GOOGLE_MAPS_API_KEY
}
